/* eslint-env browser, es2020 */
// gatsby-browser.js
// Keep <html lang> and text direction in sync on client navigation.
const React = require("react");
const { MDXProvider } = require("@mdx-js/react");
const { mdxComponents } = require("./src/utils/mdxComponents");
const {
  LanguageAttributes,
  languageEntries,
  defaultLanguage,
} = require("./src/components/LanguageAttributes.js");

// Precompile for faster repeated checks
const LANG_PREFIX_RE = /^\/([A-Za-z-]+)(?:\/|$)/;

function detectLangFromPath(pathname, config) {
  const langs = Array.isArray(config) ? config : languageEntries;
  const defaultLangCode = langs[0]?.code ?? defaultLanguage.code ?? "en";
  const map = new Map(langs.map((l) => [String(l.code), l]));

  let code = defaultLangCode;
  if (typeof pathname === "string") {
    const m = LANG_PREFIX_RE.exec(pathname);
    if (m && map.has(m[1])) code = m[1];
  }
  const cfg = map.get(code) || map.get(defaultLangCode) || {};
  return {
    lang: cfg.hrefLang || cfg.code || "en",
    dir: cfg.langDir || "ltr",
  };
}

function applyHtmlLang(attrs) {
  if (typeof document === "undefined") return;
  const el = document.documentElement;
  if (!el) return;
  if (attrs.lang) el.setAttribute("lang", attrs.lang);
  if (attrs.dir) el.setAttribute("dir", attrs.dir);
}

exports.onInitialClientRender = () => {
  try {
    const i18nConfig = require("./i18n/config.json");
    const pathname =
      (typeof globalThis !== "undefined" && globalThis.location?.pathname) ||
      "";
    const { lang, dir } = detectLangFromPath(pathname, i18nConfig);
    applyHtmlLang({ lang, dir });
    // Mark app as hydrated for E2E tests to wait on a stable DOM
    if (typeof document !== "undefined") {
      document.documentElement.dataset.appReady = "1";
    }
  } catch {}
};

// Claim manual scroll control as early as possible so the browser never
// auto-restores a previous position on pushState navigations.
exports.onClientEntry = () => {
  if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }
};

exports.onRouteUpdate = ({ location }) => {
  try {
    const i18nConfig = require("./i18n/config.json");
    const { lang, dir } = detectLangFromPath(
      location?.pathname || "",
      i18nConfig
    );
    applyHtmlLang({ lang, dir });
    if (typeof document !== "undefined") {
      document.documentElement.dataset.appReady = "1";
    }
  } catch {}

  if (typeof window === "undefined" || location.hash) return;

  // Re-assert manual scroll control and scroll to top via a double rAF.
  // Firefox iOS applies its own scroll restoration one frame after pushState,
  // potentially overriding a synchronous scrollTo(0,0). The double rAF ensures
  // our scroll runs in the second frame, after Firefox iOS has had its turn.
  if ("scrollRestoration" in window.history) {
    window.history.scrollRestoration = "manual";
  }
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      window.scrollTo(0, 0);
    });
  });
};

// For hash navigation let Gatsby scroll to the target element.
// For plain navigation onRouteUpdate handles the scroll, so return false to
// prevent Gatsby from calling scrollTo(0,0) in the same frame (which would
// fire before the double-rAF and potentially race with Firefox iOS).
exports.shouldUpdateScroll = ({ routerProps: { location } }) => {
  if (location.hash) {
    return location.hash;
  }
  return false;
};

exports.wrapRootElement = ({ element }) =>
  React.createElement(MDXProvider, { components: mdxComponents }, element);

exports.wrapPageElement = ({ element, props }) =>
  React.createElement(
    LanguageAttributes,
    { pageContext: props?.pageContext },
    element
  );
